import React from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./helpers/i18n"; 
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Button,
  useMediaQuery,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Menu,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import BuildIcon from "@mui/icons-material/Build";
import FolderIcon from "@mui/icons-material/Folder";
import EmailIcon from "@mui/icons-material/Email";
import "@fontsource/philosopher"; 
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "./design-system/theme";
import LanguageSwitcher from "./helpers/LanguageSwitcher";
import ServicesPage from "./features/services/ServicesPage";
import CaseDetail from "./features/services/CaseDetail";
import ContactPage from "./features/contact/ContactPage";
import HomePage from "./features/home/HomePage";
import BlogPage from "./features/blog/BlogPage";
import BlogDetail from "./features/blog/BlogDetail";
import ActivitiesPage from "./features/activities/ActivitiesPage";
import ActivityDetail from "./features/activities/ActivityDetail";
import ExperiencePage from "./features/experience/ExperiencePage";
import ProjectsPage from "./features/projects/ProjectsPage";
import TechnologiesPage from "./features/technologies/TechnologiesPage";
import CasesPage from "./features/services/CasesPage";
import LoginPage from "./features/auth/LoginPage";
import AdminDashboard from "./features/admin/AdminDashboard";
import AdminProjects from "./features/admin/AdminProjects";
import AdminBlog from "./features/admin/AdminBlog";
import AdminExperiences from "./features/admin/AdminExperiences";
import AdminActivities from "./features/admin/AdminActivities";
import AdminTechnologies from "./features/admin/AdminTechnologies";
import AdminMessages from "./features/admin/AdminMessages";
import AdminSkills from "./features/admin/AdminSkills";
import AdminCases from "./features/admin/AdminCases";
import AdminCVAdaptation from "./features/admin/AdminCVAdaptation";
import GuestLogin from "./features/admin/GuestLogin";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Footer from "./components/layout/Footer";
import SalesTunnel from "./features/sales/SalesTunnel";
import PrivacyPolicyPage from "./features/legal/PrivacyPolicyPage";
import TermsOfServicePage from "./features/legal/TermsOfServicePage";
import { useUIStore } from "./store/ui.store";
import { useAuth } from "./hooks/useAuth";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const MainLayout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const { isDrawerOpen, setDrawerOpen } = useUIStore();
  const { isAuthenticated } = useAuth();
  const [expertiseAnchor, setExpertiseAnchor] = React.useState(null);
  const [workAnchor, setWorkAnchor] = React.useState(null);

  const isContactPage = location.pathname === '/contact';
  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login' || location.pathname === '/guest/login';
  const isTunnelPage = location.pathname === '/tunnel' || location.pathname === '/ai-stack-consultant' || location.pathname === '/guest/cv-adaptation';

  const handleOpenExpertise = (event) => setExpertiseAnchor(event.currentTarget);
  const handleCloseExpertise = () => setExpertiseAnchor(null);
  const handleOpenWork = (event) => setWorkAnchor(event.currentTarget);
  const handleCloseWork = () => setWorkAnchor(null);

  const toggleDrawer = (state) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setDrawerOpen(state);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#111827] overflow-x-hidden font-philosopher">
      <Helmet>
        <title>Alexandre SANOU | Full Stack Data Scientist</title>
        <meta name="description" content="Portfolio of Alexandre SANOU, Full Stack Data Scientist specialized in AI, Machine Learning and Web Development." />
      </Helmet>

      <a href="#main-content" className="skip-link">
        {t("skipToContent", "Skip to content")}
      </a>

      <div className="fixed top-[10px] left-[10px] z-[1500] p-[5px] rounded-lg transition-all duration-300 ease-in-out">
        <LanguageSwitcher />
      </div>

      <header>
        {!isTunnelPage && (
          <AppBar position="fixed" component="nav" aria-label="Main navigation" sx={{ backgroundColor: { xs: "transparent", sm: "#111827" }, boxShadow: "none", zIndex: 1200 }}>
          <Toolbar sx={{ display: "flex", justifyContent: isMobile ? "flex-end" : "center", padding: "10px" }}>
            {isMobile ? (
              <IconButton onClick={toggleDrawer(true)} sx={{ color: "white", backgroundColor: "rgba(36, 33, 33, 0.5)", backdropFilter: "blur(10px)", "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" } }}>
                <MenuIcon fontSize="large" />
              </IconButton>
            ) : (
              <div className="flex gap-4">
                <Link to="/"><Button sx={{ color: "white" }}>{t("navBarMenu.home")}</Button></Link>
                <Link to="/services"><Button sx={{ color: "white" }}>{t("navBarMenu.services")}</Button></Link>
                
                <Button 
                  sx={{ color: "white" }} 
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleOpenExpertise}
                >
                  {t("navBarMenu.expertise.title")}
                </Button>
                <Menu
                  anchorEl={expertiseAnchor}
                  open={Boolean(expertiseAnchor)}
                  onClose={handleCloseExpertise}
                  sx={{ "& .MuiPaper-root": { backgroundColor: "#2b2f36", color: "white" } }}
                >
                  <MenuItem onClick={handleCloseExpertise} component={Link} to="/blog">{t("navBarMenu.expertise.blog")}</MenuItem>
                  <MenuItem onClick={handleCloseExpertise} component={Link} to="/activities">{t("navBarMenu.expertise.activities")}</MenuItem>
                  <MenuItem onClick={handleCloseExpertise} component={Link} to="/cases">{t("navBarMenu.expertise.cases")}</MenuItem>
                </Menu>

                <Button 
                  sx={{ color: "white" }} 
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleOpenWork}
                >
                  {t("navBarMenu.work.title")}
                </Button>
                <Menu
                  anchorEl={workAnchor}
                  open={Boolean(workAnchor)}
                  onClose={handleCloseWork}
                  sx={{ "& .MuiPaper-root": { backgroundColor: "#2b2f36", color: "white" } }}
                >
                  <MenuItem onClick={handleCloseWork} component={Link} to="/experience">{t("navBarMenu.work.experience")}</MenuItem>
                  <MenuItem onClick={handleCloseWork} component={Link} to="/projects">{t("navBarMenu.work.projects")}</MenuItem>
                  <MenuItem onClick={handleCloseWork} component={Link} to="/technologies">{t("navBarMenu.work.technologies")}</MenuItem>
                </Menu>

                <Link to="/contact"><Button sx={{ color: "white" }}>{t("navBarMenu.contact")}</Button></Link>
                <Button 
                  variant="contained"
                  href="https://calendly.com/sanoualexandreism/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    bgcolor: "#D4AF37", 
                    color: "#0B0F14", 
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#B8962E" }
                  }}
                >
                  {t("navBarMenu.booking")}
                </Button>
                {isAuthenticated && (
                  <Link to="/admin">
                    <Button sx={{ color: "#D4AF37", fontWeight: 600 }} startIcon={<AdminPanelSettingsIcon />}>
                      Admin
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </Toolbar>
          </AppBar>
        )}
      </header>

      <Drawer anchor="right" open={isDrawerOpen} onClose={toggleDrawer(false)} sx={{ "& .MuiDrawer-paper": { backgroundColor: "rgba(11, 15, 20, 0.95)", backdropFilter: "blur(10px)", width: "250px" } }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/" sx={{ color: "white", textAlign: "center" }}>
              <ListItemText primary={t("navBarMenu.home")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/services" sx={{ color: "white", textAlign: "center" }}>
              <ListItemText primary={t("navBarMenu.services")} />
            </ListItemButton>
          </ListItem>
          
          <ListItem sx={{ color: "#D4AF37", pt: 2, pb: 0.5 }}><ListItemText primary={t("navBarMenu.expertise.title")} /></ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/blog" sx={{ color: "white", textAlign: "center", pl: 4 }}>
              <ListItemText primary={t("navBarMenu.expertise.blog")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/activities" sx={{ color: "white", textAlign: "center", pl: 4 }}>
              <ListItemText primary={t("navBarMenu.expertise.activities")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/cases" sx={{ color: "white", textAlign: "center", pl: 4 }}>
              <ListItemText primary={t("navBarMenu.expertise.cases")} />
            </ListItemButton>
          </ListItem>

          <ListItem sx={{ color: "#D4AF37", pt: 2, pb: 0.5 }}><ListItemText primary={t("navBarMenu.work.title")} /></ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/experience" sx={{ color: "white", textAlign: "center", pl: 4 }}>
              <ListItemText primary={t("navBarMenu.work.experience")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/projects" sx={{ color: "white", textAlign: "center", pl: 4 }}>
              <ListItemText primary={t("navBarMenu.work.projects")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/technologies" sx={{ color: "white", textAlign: "center", pl: 4 }}>
              <ListItemText primary={t("navBarMenu.work.technologies")} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mt: 2 }}>
            <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/contact" sx={{ color: "white", textAlign: "center" }}>
              <ListItemText primary={t("navBarMenu.contact")} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ mt: 2, px: 2 }}>
            <Button 
              fullWidth
              variant="contained"
              href="https://calendly.com/sanoualexandreism/30min"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: "#D4AF37", 
                color: "#0B0F14", 
                fontWeight: 700,
                "&:hover": { bgcolor: "#B8962E" }
              }}
            >
              {t("navBarMenu.booking")}
            </Button>
          </ListItem>
          {isAuthenticated && (
            <ListItem disablePadding sx={{ mt: 2 }}>
              <ListItemButton onClick={() => setDrawerOpen(false)} component={Link} to="/admin" sx={{ color: "#D4AF37", textAlign: "center" }}>
                <ListItemText primary="Admin" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Drawer>

      <main id="main-content" className="flex-grow pt-16 pb-20 md:pb-0" role="main">
        {children}
      </main>

      {!isContactPage && !isAdminPage && !isTunnelPage && <Footer />}

      {isMobile && !isTunnelPage && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300 }} elevation={3}>
          <BottomNavigation
            showLabels
            value={window.location.pathname}
            onChange={(event, newValue) => {
              // Navigation is handled by Link or programmatic navigate
            }}
          >
            <BottomNavigationAction 
              label={t("navBarMenu.home")} 
              value="/" 
              icon={<HomeIcon />} 
              component={Link} 
              to="/" 
            />
            <BottomNavigationAction 
              label={t("navBarMenu.services")} 
              value="/services" 
              icon={<BuildIcon />} 
              component={Link} 
              to="/services" 
            />
            <BottomNavigationAction 
              label={t("navBarMenu.work.projects")} 
              value="/projects" 
              icon={<FolderIcon />} 
              component={Link} 
              to="/projects" 
            />
            <BottomNavigationAction 
              label={t("navBarMenu.contact")} 
              value="/contact" 
              icon={<EmailIcon />} 
              component={Link} 
              to="/contact" 
            />
          </BottomNavigation>
        </Paper>
      )}
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <MainLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<CaseDetail />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/activities" element={<ActivitiesPage />} />
            <Route path="/activities/:id" element={<ActivityDetail />} />
            <Route path="/experience" element={<ExperiencePage />} />
            <Route path="/technologies" element={<TechnologiesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/tunnel" element={<ProtectedRoute><SalesTunnel /></ProtectedRoute>} />
            <Route path="/ai-stack-consultant" element={<SalesTunnel />} />
            <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/projects" element={<ProtectedRoute requireAdmin={true}><AdminProjects /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute requireAdmin={true}><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/experiences" element={<ProtectedRoute requireAdmin={true}><AdminExperiences /></ProtectedRoute>} />
            <Route path="/admin/activities" element={<ProtectedRoute requireAdmin={true}><AdminActivities /></ProtectedRoute>} />
            <Route path="/admin/technologies" element={<ProtectedRoute requireAdmin={true}><AdminTechnologies /></ProtectedRoute>} />
            <Route path="/admin/messages" element={<ProtectedRoute requireAdmin={true}><AdminMessages /></ProtectedRoute>} />
            <Route path="/admin/skills" element={<ProtectedRoute requireAdmin={true}><AdminSkills /></ProtectedRoute>} />
            <Route path="/admin/cases" element={<ProtectedRoute requireAdmin={true}><AdminCases /></ProtectedRoute>} />
            <Route path="/admin/cv-adaptation" element={<ProtectedRoute requireAdmin={true}><AdminCVAdaptation /></ProtectedRoute>} />
            <Route path="/guest/login" element={<GuestLogin />} />
            <Route path="/guest/cv-adaptation" element={<AdminCVAdaptation isGuest={true} />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
          </Routes>
        </MainLayout>
      </Router>
    </ThemeProvider>
  );
};

export default App;
